// Confirmation dialog for Generate Occurrences. Shows an exact forecast
// (count + first/middle/last date strip) before the user commits to the
// write mutation. Detects re-runs via `useTrackingItemsByParent` so the
// body distinguishes "N existing + M new" from a first-time generation.
// 104-cap amber warning when the backend reports `capped: true`.
//
// The forecast shows an exact count (not "~N"), a first-3 + last date
// strip, a re-run detection message, and a cap warning. The contract with
// the backend is `{count, occurrenceDates, capped}`.
//
// Type-aware: the dialog accepts a `type` prop ("deliverable" | "meeting")
// so the same dialog works for both recurrence-capable tracking surfaces.
// Hooks (preview + by-parent listing) thread the type through; copy uses a
// type-derived noun so error/info text reads naturally on both surfaces.

import { useMemo, useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import LoadingSpinner from "../../../common/LoadingSpinner";
import {
  usePreviewOccurrences,
  useTrackingItemsByParent,
} from "../../../../hooks/useTracking";
import { serialiseRule } from "./serialiseRule";
import type { RecurrenceRule } from "../../../../types";

/**
 * Minimal item shape the dialog reads. Both `Deliverable` and `Meeting`
 * carry id + name + optional recurrenceRule, so the dialog accepts a
 * structural superset and avoids cross-type casts at every call site.
 */
export interface GenerateOccurrencesItem {
  id: string;
  name: string;
  recurrenceRule?: RecurrenceRule;
}

type RecurrenceCapableType = "deliverable" | "meeting";

const TYPE_NOUN: Record<RecurrenceCapableType, string> = {
  deliverable: "deliverable",
  meeting: "meeting",
};

interface GenerateOccurrencesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contractId: string;
  /** Tracking type — drives the preview + by-parent hook calls and the
   *  on-screen noun. Defaults to "deliverable" for backwards-compat with
   *  the existing DeliverableTab caller. */
  type?: RecurrenceCapableType;
  item: GenerateOccurrencesItem | null;
  /** When `archiveOrphanIds` is supplied (and non-empty), the caller is
   *  expected to archive those occurrence items as part of the confirm
   *  flow (the dialog detects them but delegates the archive call to the
   *  parent tab which owns the archive mutation). */
  onConfirm: (archiveOrphanIds?: string[]) => Promise<void>;
  isSubmitting?: boolean;
}

function formatDate(v: string): string {
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU");
  } catch {
    return v;
  }
}

function buildDateStrip(dates: string[]): string {
  if (dates.length === 0) return "";
  if (dates.length <= 4) {
    return dates.map(formatDate).join(" · ");
  }
  const first = dates.slice(0, 3).map(formatDate).join(" · ");
  return `${first} … Last: ${formatDate(dates[dates.length - 1])}`;
}

export default function GenerateOccurrencesDialog({
  isOpen,
  onClose,
  contractId,
  type = "deliverable",
  item,
  onConfirm,
  isSubmitting,
}: GenerateOccurrencesDialogProps) {
  const itemId = item?.id ?? "";
  const ruleKey = serialiseRule(item?.recurrenceRule);
  const noun = TYPE_NOUN[type];

  const preview = usePreviewOccurrences(
    contractId,
    type,
    itemId,
    ruleKey,
    isOpen && !!item?.recurrenceRule,
  );
  const existing = useTrackingItemsByParent(contractId, type, itemId);

  // Classify existing children against the current rule:
  //   - MATCHED: existing occurrence whose date IS in the rule's date list
  //     (upsert is a no-op for these — they stay as-is, keeping status/RAG/
  //     evidence from prior user edits).
  //   - ORPHAN: existing occurrence whose date is NOT in the rule's date
  //     list. The rule has moved (e.g. start date edited) and these records
  //     no longer reflect the schedule. Backend never deletes them on
  //     generate, so they'd linger as "ghost" rows unless the user opts
  //     in to archive them here.
  const {
    existingCount,
    matchedCount,
    orphans,
    newCount,
  } = useMemo(() => {
    const existingItems = existing.data?.items ?? [];
    const ruleDates = new Set(preview.data?.occurrenceDates ?? []);
    const matched = existingItems.filter(
      (i) => i.occurrenceDate && ruleDates.has(i.occurrenceDate),
    );
    const orph = existingItems.filter(
      (i) => !i.occurrenceDate || !ruleDates.has(i.occurrenceDate),
    );
    return {
      existingCount: existingItems.length,
      matchedCount: matched.length,
      orphans: orph,
      newCount: Math.max(0, (preview.data?.count ?? 0) - matched.length),
    };
  }, [existing.data, preview.data]);

  const isReRun = existingCount > 0;

  // Opt-in: archive orphans alongside the generate call. Reset when the
  // dialog closes or when the rule changes so each new dialog open starts
  // unchecked.
  const [archiveOrphans, setArchiveOrphans] = useState(false);
  const archiveSeedKey = isOpen ? ruleKey : null;
  const [archiveSeededFor, setArchiveSeededFor] = useState<string | null>(null);
  if (archiveSeedKey !== archiveSeededFor) {
    setArchiveSeededFor(archiveSeedKey);
    setArchiveOrphans(false);
  }

  const dateStrip = useMemo(
    () => (preview.data ? buildDateStrip(preview.data.occurrenceDates) : ""),
    [preview.data],
  );

  const missingRule = !item?.recurrenceRule;

  const handleConfirm = () =>
    onConfirm(
      archiveOrphans && orphans.length > 0
        ? orphans.map((o) => o.id)
        : undefined,
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate occurrences"
      maxWidth="sm:max-w-xl"
    >
      <div className="space-y-4">
        {missingRule && (
          <Alert variant="info">
            This {noun} does not have a recurrence rule. Add one before
            generating occurrences.
          </Alert>
        )}

        {!missingRule && preview.isPending && (
          <div className="flex justify-center py-6">
            <LoadingSpinner size="md" />
          </div>
        )}

        {!missingRule && preview.error ? (
          <Alert variant="error">
            {preview.error instanceof Error
              ? preview.error.message
              : "Failed to preview occurrences."}
          </Alert>
        ) : null}

        {!missingRule && preview.data && (
          <>
            {preview.data.count === 0 ? (
              <Alert variant="info">
                No occurrences will be generated. Check the recurrence rule
                (e.g. end date may be in the past).
              </Alert>
            ) : (
              <>
                <p
                  className="text-body text-slate-700 dark:text-slate-300"
                  data-testid="occurrence-count"
                >
                  The current schedule has{" "}
                  <strong>{preview.data.count}</strong>{" "}
                  occurrence{preview.data.count === 1 ? "" : "s"}.
                </p>
                {isReRun && (
                  <div
                    className="text-body-sm text-slate-600 dark:text-slate-400 space-y-1"
                    data-testid="rerun-detection"
                  >
                    <p>
                      <strong>{newCount}</strong> new will be created;{" "}
                      <strong>{matchedCount}</strong> existing occurrence
                      {matchedCount === 1 ? "" : "s"} match{" "}
                      {matchedCount === 1 ? "es" : ""} the schedule and will
                      be kept.
                    </p>
                    {orphans.length > 0 && (
                      <p data-testid="orphan-count">
                        <strong className="text-amber-600 dark:text-amber-400">
                          {orphans.length}
                        </strong>{" "}
                        existing occurrence{orphans.length === 1 ? "" : "s"}{" "}
                        no longer match the schedule (likely from a previous
                        rule). By default {orphans.length === 1 ? "it" : "they"}{" "}
                        will remain in the list.
                      </p>
                    )}
                  </div>
                )}
                {dateStrip && (
                  <p
                    className="text-caption text-slate-500 dark:text-slate-400"
                    data-testid="date-strip"
                  >
                    First: {dateStrip}
                  </p>
                )}
                {orphans.length > 0 && (
                  <label className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-body-sm text-amber-800 dark:border-amber-900/30 dark:bg-amber-900/10 dark:text-amber-200">
                    <input
                      type="checkbox"
                      checked={archiveOrphans}
                      onChange={(e) => setArchiveOrphans(e.target.checked)}
                      disabled={isSubmitting}
                      className="mt-0.5 accent-brand-600"
                      data-testid="archive-orphans-checkbox"
                    />
                    <span>
                      Also archive the {orphans.length} existing occurrence
                      {orphans.length === 1 ? "" : "s"} that no longer match
                      the schedule. Archive reason:{" "}
                      <em>&ldquo;Superseded by updated recurrence rule&rdquo;</em>.
                    </span>
                  </label>
                )}
                {preview.data.capped && (
                  <Alert variant="warning">
                    Capped at 104 occurrences. Shorten the end date or
                    reduce frequency to generate fewer.
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={
              missingRule ||
              preview.isPending ||
              !!preview.error ||
              // Disable when there's genuinely nothing to do: no new dates
              // AND the user hasn't ticked "archive orphans".
              (newCount === 0 && !(archiveOrphans && orphans.length > 0)) ||
              isSubmitting
            }
            loading={isSubmitting}
          >
            {archiveOrphans && orphans.length > 0 && newCount === 0
              ? "Archive orphans"
              : "Generate"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
